import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Box,
} from '@mui/material';
import { ExpandMore, ExpandLess, ContentCopy } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { faker } from '@faker-js/faker';

import {
  AdminQaSeedUserService,
  Company,
  CompanyService,
  SeedUserRequest,
  SeedUserResponse,
  UserType,
  AvailabilityType,
  PostFrequency,
  ParentSignupVerticalTypeId,
} from 'us-web-services';

import PageStyles from '../../styles/PageStyles';
import DisplayService from '../../util/DisplayService';

interface UserPreset {
  id: string;
  label: string;
  userType: UserType;
  signupVerticalTypeId?: ParentSignupVerticalTypeId;
  availabilityType?: AvailabilityType;
}

const USER_PRESETS: UserPreset[] = [
  {
    id: 'basic-parent',
    label: 'Basic Parent',
    userType: 'PARENT',
    signupVerticalTypeId: ParentSignupVerticalTypeId.CHILD_CARE,
  },
  {
    id: 'pet-dog-parent',
    label: 'Pet Care (Dog)',
    userType: 'PARENT',
    signupVerticalTypeId: ParentSignupVerticalTypeId.PET_CARE_DOG,
  },
  {
    id: 'pet-cat-parent',
    label: 'Pet Care (Cat)',
    userType: 'PARENT',
    signupVerticalTypeId: ParentSignupVerticalTypeId.PET_CARE_CAT,
  },
  {
    id: 'senior-parent',
    label: 'Senior Care Parent',
    userType: 'PARENT',
    signupVerticalTypeId: ParentSignupVerticalTypeId.SENIOR_CARE,
  },
  {
    id: 'occasional-sitter',
    label: 'Occasional Sitter',
    userType: 'SITTER',
    availabilityType: 'OCCASIONAL',
  },
  {
    id: 'parttime-sitter',
    label: 'Part-Time Sitter',
    userType: 'SITTER',
    availabilityType: 'PART_TIME',
  },
  {
    id: 'fulltime-sitter',
    label: 'Full-Time Sitter',
    userType: 'SITTER',
    availabilityType: 'FULL_TIME',
  },
];

const VERTICAL_TYPE_OPTIONS: { value: ParentSignupVerticalTypeId; label: string }[] = [
  { value: ParentSignupVerticalTypeId.CHILD_CARE, label: 'Child Care' },
  { value: ParentSignupVerticalTypeId.PET_CARE_DOG, label: 'Pet Care (Dog)' },
  { value: ParentSignupVerticalTypeId.PET_CARE_CAT, label: 'Pet Care (Cat)' },
  { value: ParentSignupVerticalTypeId.PET_CARE_OTHER, label: 'Pet Care (Other)' },
  { value: ParentSignupVerticalTypeId.SENIOR_CARE, label: 'Senior Care' },
  { value: ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES, label: 'Household Services' },
];

const getNextDay = (): string => {
  const tomorrow = new Date();

  tomorrow.setDate(tomorrow.getDate() + 1);

  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const TEST_EMAIL_PREFIX = 'test+';
const ALLOWED_EMAIL_DOMAIN = '@urbansitter.net';

const isValidTestEmail = (emailToCheck: string): boolean => {
  if (!emailToCheck) return true;

  return emailToCheck.startsWith(TEST_EMAIL_PREFIX) && emailToCheck.endsWith(ALLOWED_EMAIL_DOMAIN);
};

function SeedTestUser() {
  const { classes } = PageStyles();

  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [userType, setUserType] = useState<UserType>('PARENT');
  const [firstName, setFirstName] = useState(faker.person.firstName());
  const [lastName, setLastName] = useState(faker.person.lastName());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('TestPassword123!');
  const [zip, setZip] = useState('94118');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('1990-01-15');
  const [signupVerticalTypeId, setSignupVerticalTypeId] = useState<ParentSignupVerticalTypeId>(
    ParentSignupVerticalTypeId.CHILD_CARE,
  );
  const [availabilityType, setAvailabilityType] = useState<AvailabilityType>('OCCASIONAL');
  const [count, setCount] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [createPost, setCreatePost] = useState(false);
  const [postDate, setPostDate] = useState(getNextDay());
  const [postFrequency, setPostFrequency] = useState<PostFrequency>('ONE_TIME');
  const [postDescription, setPostDescription] = useState('');

  const [passBackgroundCheck, setPassBackgroundCheck] = useState(true);
  const [passIdentityCheck, setPassIdentityCheck] = useState(true);

  const [companyId, setCompanyId] = useState<number | undefined>(undefined);
  const [companies, setCompanies] = useState<Company[]>([]);
  const companiesFetchedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SeedUserResponse | null>(null);
  const [resultPassword, setResultPassword] = useState('');

  const generateFakeName = () => {
    setFirstName(faker.person.firstName());
    setLastName(faker.person.lastName());
  };

  const generateEmail = (type?: UserType) => {
    const timestamp = Date.now();
    const typePrefix = (type || userType).toLowerCase();

    return `${TEST_EMAIL_PREFIX}${typePrefix}-${timestamp}${ALLOWED_EMAIL_DOMAIN}`;
  };

  const handleGenerateEmail = () => {
    setEmail(generateEmail());
  };

  useEffect(() => {
    setEmail(generateEmail());
  }, []);

  useEffect(() => {
    if (userType !== 'PARENT' || !showAdvanced || companiesFetchedRef.current) {
      return;
    }

    const fetchCompanies = async () => {
      companiesFetchedRef.current = true;

      try {
        const response = await CompanyService.getByFilter({
          params: { limit: 500 },
        });

        setCompanies(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      }
    };

    fetchCompanies();
  }, [userType, showAdvanced]);

  const generateFakePost = () => {
    setPostDescription(faker.lorem.sentences(2));
  };

  const handlePresetClick = (preset: UserPreset) => {
    setSelectedPresetId(preset.id);
    setUserType(preset.userType);
    if (preset.signupVerticalTypeId) setSignupVerticalTypeId(preset.signupVerticalTypeId);
    if (preset.availabilityType) setAvailabilityType(preset.availabilityType);
    generateFakeName();
    setEmail(generateEmail(preset.userType));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const finalEmail = email || generateEmail();

    if (!isValidTestEmail(finalEmail)) {
      toast.error(`Email must start with '${TEST_EMAIL_PREFIX}' and end with '${ALLOWED_EMAIL_DOMAIN}'`);

      return;
    }

    setLoading(true);
    setResult(null);

    const request: SeedUserRequest = {
      userType,
      firstName,
      lastName,
      email: finalEmail,
      password,
      zip,
      phone: phone || undefined,
      dob: userType === 'SITTER' ? dob : undefined,
      signupVerticalTypeId: userType === 'PARENT' ? signupVerticalTypeId : undefined,
      availabilityType: userType === 'SITTER' ? availabilityType : undefined,
      count,
      createPost: userType === 'PARENT' ? createPost : undefined,
      postDate:
        userType === 'PARENT' && createPost && postFrequency === 'ONE_TIME'
          ? postDate
          : undefined,
      postFrequency: userType === 'PARENT' && createPost ? postFrequency : undefined,
      postDescription: userType === 'PARENT' && createPost ? postDescription : undefined,
      passBackgroundCheck: userType === 'SITTER' ? passBackgroundCheck : undefined,
      passIdentityCheck: userType === 'PARENT' ? passIdentityCheck : undefined,
      companyId: userType === 'PARENT' ? companyId : undefined,
    };

    try {
      const response = await AdminQaSeedUserService.create(request);

      setResult(response.data.data);
      setResultPassword(password);
      toast.success(response.data.data.message);
    } catch (error) {
      const displayedError = DisplayService.getErrorResponse(
        error,
        'Failed to create test user(s)',
      );

      toast.error(displayedError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper className={classes.paper}>
      <Typography variant='h6' gutterBottom>
        Seed Test User
      </Typography>
      <Typography variant='body2' color='textSecondary' paragraph>
        Create test users with completed NUX for QA testing. Users are created with
        emails starting with &quot;test+&quot; for easy identification.
      </Typography>

      <Box mb={3}>
        <Typography variant='subtitle2' gutterBottom>
          Quick Presets
        </Typography>
        <Box display='flex' flexWrap='wrap' gap={1}>
          {USER_PRESETS.map((preset) => (
            <Chip
              key={preset.id}
              label={preset.label}
              onClick={() => handlePresetClick(preset)}
              color={selectedPresetId === preset.id ? 'primary' : 'default'}
              variant={selectedPresetId === preset.id ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={userType}
                label='User Type'
                onChange={(e) => {
                  setUserType(e.target.value as UserType);
                  setSelectedPresetId(undefined);
                }}
              >
                <MenuItem value='PARENT'>Parent</MenuItem>
                <MenuItem value='SITTER'>Sitter</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label='First Name'
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label='Last Name'
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              variant='outlined'
              onClick={generateFakeName}
              fullWidth
              style={{ height: '56px' }}
            >
              Random Name
            </Button>
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              label='Email'
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='test+example@urbansitter.net'
              helperText="Must start with 'test+' and use @urbansitter.net - leave blank to auto-generate"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Button
              variant='outlined'
              onClick={handleGenerateEmail}
              fullWidth
              style={{ height: '56px' }}
            >
              Generate Email
            </Button>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Password'
              type='password'
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Zip Code'
              fullWidth
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              required
            />
          </Grid>

          {userType === 'SITTER' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label='Date of Birth'
                type='date'
                fullWidth
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          )}

          {userType === 'PARENT' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Care Type</InputLabel>
                <Select
                  value={signupVerticalTypeId}
                  label='Care Type'
                  onChange={(e) => setSignupVerticalTypeId(e.target.value as ParentSignupVerticalTypeId)}
                >
                  {VERTICAL_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              label='Number of Users'
              type='number'
              fullWidth
              value={count}
              onChange={(e) => setCount(Math.min(50, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              inputProps={{ min: 1, max: 50 }}
              helperText='Create multiple users (1-50)'
            />
          </Grid>

          {userType === 'SITTER' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Availability</InputLabel>
                <Select
                  value={availabilityType}
                  label='Availability'
                  onChange={(e) => setAvailabilityType(e.target.value as AvailabilityType)}
                >
                  <MenuItem value='OCCASIONAL'>Occasional</MenuItem>
                  <MenuItem value='PART_TIME'>Part-Time</MenuItem>
                  <MenuItem value='FULL_TIME'>Full-Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              onClick={() => setShowAdvanced(!showAdvanced)}
              endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
            >
              Advanced Options
            </Button>
            <Collapse in={showAdvanced}>
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Phone'
                      fullWidth
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </Grid>

                  {userType === 'PARENT' && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' sx={{ mt: 1 }}>
                          Post Options
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={createPost}
                              onChange={(e) => {
                                setCreatePost(e.target.checked);
                                if (e.target.checked) generateFakePost();
                              }}
                            />
                          }
                          label='Create a job post'
                        />
                      </Grid>
                      {createPost && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Post Frequency</InputLabel>
                              <Select
                                value={postFrequency}
                                label='Post Frequency'
                                onChange={(e) => setPostFrequency(e.target.value as PostFrequency)}
                              >
                                <MenuItem value='ONE_TIME'>One Time (Specific Date)</MenuItem>
                                <MenuItem value='PART_TIME'>Part-Time</MenuItem>
                                <MenuItem value='FULL_TIME'>Full-Time</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {postFrequency === 'ONE_TIME' && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label='Post Date'
                                type='date'
                                fullWidth
                                value={postDate}
                                onChange={(e) => setPostDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <TextField
                              label='Post Description'
                              fullWidth
                              multiline
                              rows={2}
                              value={postDescription}
                              onChange={(e) => setPostDescription(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Button
                              variant='outlined'
                              onClick={generateFakePost}
                              size='small'
                            >
                              Regenerate Post Content
                            </Button>
                          </Grid>
                        </>
                      )}
                    </>
                  )}

                  {userType === 'SITTER' && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' sx={{ mt: 1 }}>
                          Verification Options
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passBackgroundCheck}
                              onChange={(e) => setPassBackgroundCheck(e.target.checked)}
                            />
                          }
                          label='Pass background check'
                        />
                      </Grid>
                    </>
                  )}

                  {userType === 'PARENT' && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' sx={{ mt: 1 }}>
                          Verification Options
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passIdentityCheck}
                              onChange={(e) => setPassIdentityCheck(e.target.checked)}
                            />
                          }
                          label='Pass identity check'
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' sx={{ mt: 1 }}>
                          Company/Benefit Linking (BD)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Company</InputLabel>
                          <Select
                            value={companyId || ''}
                            label='Company'
                            onChange={(e) => {
                              const val = e.target.value;

                              setCompanyId(val ? Number(val) : undefined);
                            }}
                          >
                            <MenuItem value=''>
                              <em>None</em>
                            </MenuItem>
                            {companies.map((company) => (
                              <MenuItem key={company.id} value={company.id}>
                                {company.name || company.displayName || `Company ${company.id}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </Grid>
        </Grid>

        <Box mt={3}>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Creating...' : `Create ${count > 1 ? `${count} Users` : 'User'}`}
          </Button>
        </Box>
      </form>

      {result && (
        <Box mt={3}>
          <Alert severity='success'>
            <Typography variant='subtitle2'>{result.message}</Typography>
            <Box mt={1}>
              {result.users.map((user) => (
                <Box
                  key={user.userId}
                  display='flex'
                  alignItems='center'
                  flexWrap='wrap'
                  gap={2}
                  sx={{ mb: 1, py: 0.5 }}
                >
                  <Typography variant='body2'>
                    <strong>ID:</strong> {user.userId}
                  </Typography>
                  <Box display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      <strong>Email:</strong> {user.email}
                    </Typography>
                    <Button
                      size='small'
                      onClick={() => {
                        navigator.clipboard.writeText(user.email);
                        toast.success('Email copied!');
                      }}
                      sx={{ ml: 0.5, minWidth: 'auto', p: 0.25 }}
                    >
                      <ContentCopy fontSize='small' />
                    </Button>
                  </Box>
                  <Box display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      <strong>Password:</strong> {resultPassword}
                    </Typography>
                    <Button
                      size='small'
                      onClick={() => {
                        navigator.clipboard.writeText(resultPassword);
                        toast.success('Password copied!');
                      }}
                      sx={{ ml: 0.5, minWidth: 'auto', p: 0.25 }}
                    >
                      <ContentCopy fontSize='small' />
                    </Button>
                  </Box>
                  <Typography variant='body2'>
                    <strong>Type:</strong> {user.userType}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        </Box>
      )}
    </Paper>
  );
}

export default SeedTestUser;
